import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CreditCard, FolderKanban, LogOut, User } from 'lucide-react';
import { toast } from 'sonner';
import { ArterraLogo } from '@/components/ui/ArterraLogo';
import { NotificationBell } from '@/components/raises/owner/NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import '@/components/raises/design/tokens.css';

interface OwnerLayoutProps {
  children: ReactNode;
  /** Full-width mount seam directly below the masthead (for a real M2 banner). */
  ownerBanner?: ReactNode;
}

/**
 * Minimal chrome for the scoped `raise_owner` persona.
 *
 * A claimed-raise owner is walled off from the rest of the Arterra platform, so
 * this layout deliberately renders NO `AppSidebar` / developer navigation — only:
 *   - the Arterra brand (links to the owner console),
 *   - a single "My Projects" link to `/owner`,
 *   - a profile link + logout control.
 *
 * Session machinery note: the idle-timeout dialog, MFA-grace banner, cross-tab
 * logout sync, token-refresh prompts and confirm dialogs are mounted ONCE at the
 * App root (App.tsx — siblings of the layout split) and are driven by the
 * `AuthManager` singleton activated through `useAuth()`. They are therefore
 * shared by every layout (Public / Authenticated / Owner). This component must
 * NOT re-mount them — doing so would double-register the window listeners and
 * fire duplicate dialogs. Calling `useAuth()` here keeps the owner session wired
 * into that global machinery (idle timeout, cross-tab logout, etc.).
 */
function OwnerNavLink({
  to,
  label,
  icon: Icon,
  active,
}: {
  to: string;
  label: string;
  icon: typeof FolderKanban;
  active: boolean;
}) {
  // Ledger masthead is ink → the nav sits on the dark band: gold-underlined active
  // link, paper-tinted idle links (the mockups' `.nav a.link`).
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2 rounded-[2px] px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'text-[color:var(--l-paper)] [box-shadow:inset_0_-2px_0_var(--l-gold)]'
          : 'text-[color:rgba(247,245,240,0.72)] hover:text-[color:var(--l-paper)] hover:bg-white/5',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{label}</span>
    </Link>
  );
}

export function OwnerLayout({ children, ownerBanner }: OwnerLayoutProps) {
  // useAuth() activates the AuthManager singleton (and thus the shared global
  // session machinery) for the owner session — see component doc above.
  const { user, clearCacheAndLogout } = useAuth();
  const { pathname } = useLocation();

  const email = user?.email ?? '';
  // "My Projects" is active on the console + claim surfaces, but NOT on /owner/billing
  // (which has its own nav link). Scoping it off billing keeps exactly one nav link
  // gold-underlined at a time.
  const isBilling = pathname === '/owner/billing' || pathname.startsWith('/owner/billing/');
  const isOwnerHome = !isBilling && (pathname === '/owner' || pathname.startsWith('/owner/'));

  const handleLogout = useCallback(async () => {
    try {
      await clearCacheAndLogout();
      toast.success('Logged out successfully');
    } catch {
      toast.error('Error logging out');
    }
  }, [clearCacheAndLogout]);

  return (
    <div className="raises-surface flex h-screen flex-col overflow-hidden bg-[color:var(--l-paper)]">
      {/* Ledger masthead — the ink header band with the gold ledger-rule underline
          (V1 mockup `.masthead` + the gold `::after` hairline). The dark band is the
          owner console's one scoped "instrument panel" accent per ADJUDICATION §19. */}
      <header
        className="relative flex h-14 shrink-0 items-center gap-4 bg-[color:var(--l-ink)] px-4 after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:bg-[linear-gradient(90deg,var(--l-gold),var(--l-gold-deep))] after:content-[''] sm:px-6"
        role="banner"
      >
        <Link
          to="/owner"
          className="flex shrink-0 items-center gap-2.5"
          aria-label="Arterra owner console home"
        >
          <ArterraLogo size={30} />
          <span className="ledger-serif hidden text-lg font-bold tracking-tight text-[color:var(--l-paper)] sm:inline">
            Arterra
          </span>
          <span className="ledger-sc hidden border-l border-white/15 pl-3 text-[color:rgba(247,245,240,0.6)] sm:inline">
            Owner Console
          </span>
        </Link>

        <nav className="flex items-center gap-1" aria-label="Owner Navigation">
          <OwnerNavLink to="/owner" label="My Projects" icon={FolderKanban} active={isOwnerHome} />
          <OwnerNavLink to="/owner/billing" label="Billing" icon={CreditCard} active={isBilling} />
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          {email && (
            <span
              className="hidden max-w-[180px] truncate text-xs text-[color:rgba(247,245,240,0.6)] md:inline"
              title={email}
            >
              {email}
            </span>
          )}
          <NotificationBell />
          <Link
            to="/profile"
            aria-label="Profile"
            className="flex items-center gap-2 rounded-[2px] px-3 py-1.5 text-sm font-medium text-[color:rgba(247,245,240,0.72)] transition-colors hover:bg-white/5 hover:text-[color:var(--l-paper)]"
          >
            <User className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Profile</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-[2px] px-3 py-1.5 text-sm font-medium text-[color:rgba(247,245,240,0.72)] transition-colors hover:bg-[color:rgba(155,59,50,0.25)] hover:text-[color:var(--l-paper)]"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {ownerBanner ?? null}
      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
